import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Play, Pause, Loader2, Volume2, Bell, RotateCcw, Check } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// One warm, soothing voice — no picker, no confusion. Coral is soft, professional,
// and the audio is already baked into Mongo cache so playback is instant.
const VOICES = [
  { id: 'coral', label: 'Coral', gender: 'F' },
];
const VOICE_KEY = 'meditation_voice';

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * Full-screen guided-meditation player. Mirrors the breathwork player's calm,
 * focused aesthetic but plays a Nikki-written script as spoken audio (OpenAI TTS).
 *
 * Props: meditationId (for defaults) OR script (+title/subtitle for custom),
 * estMin, onClose.
 */
const MeditationPlayer = ({ meditationId, script, title, subtitle, estMin, onClose }) => {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [meta, setMeta] = useState({ title: title || 'Meditation', subtitle: subtitle || '' });
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const mountedRef = useRef(true);
  const [ambienceOn, setAmbienceOn] = useState(false);
  const ambienceOnRef = useRef(false);
  const audioCtxRef = useRef(null);
  const ambienceMasterRef = useRef(null);
  // Ambience volume is INDEPENDENT of the voice volume — persisted in localStorage.
  // Default 0.15 (very subtle) so bowls sit gently under the voice; user can dial
  // up to 0.5 for a stronger sound-bath feel. Range: 0–0.5 (not 0–1) because
  // singing bowls have resonant peaks that would overpower speech at 1.0.
  const AMBIENCE_VOL_KEY = 'meditation_ambience_vol';
  const [ambienceVol, setAmbienceVol] = useState(() => {
    const v = parseFloat(localStorage.getItem(AMBIENCE_VOL_KEY));
    return Number.isFinite(v) ? v : 0.15;
  });
  const ambienceVolRef = useRef(ambienceVol);
  const [voice, setVoice] = useState(() => localStorage.getItem(VOICE_KEY) || 'coral');

  useEffect(() => {
    mountedRef.current = true;
    prepare();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice]);

  const prepare = useCallback(() => {
    setStatus('loading');
    const base = meditationId ? { meditation_id: meditationId } : { script, title, subtitle };
    api.meditationPrepare({ ...base, voice })
      .then((r) => {
        if (!mountedRef.current) return;
        const d = r.data || {};
        setMeta({ title: d.title || title || 'Meditation', subtitle: d.subtitle || subtitle || '' });
        setAudioUrl(`${BACKEND_URL}${d.audio_path}`);
        setStatus('ready');
      })
      .catch(() => { if (mountedRef.current) setStatus('error'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meditationId, script, title, subtitle, voice]);

  // Switch voice — persist + reload audio in the new voice (re-prepare via effect).
  const changeVoice = (vid) => {
    if (vid === voice) return;
    const a = audioRef.current;
    if (a) { a.pause(); }
    setPlaying(false);
    setCurrent(0);
    localStorage.setItem(VOICE_KEY, vid);
    setVoice(vid);
  };

  // Soft ambient bed: real Tibetan singing bowls (3-min segment from Internet
  // Archive public-domain recording, cached as static MP3). Runs on its OWN
  // <audio> element with its OWN volume so the user can dial the bowls subtle
  // without also lowering the voice (previous bug: bowls overpowered speech
  // whenever they turned the meditation up).
  const AMBIENCE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/audio/ambience/singing-bowls.mp3?v=3`;
  const ambienceElRef = useRef(null);
  const ensureAmbience = useCallback(() => {
    if (ambienceElRef.current) return;
    const el = new Audio(AMBIENCE_URL);
    el.loop = true;
    el.volume = 0;
    el.preload = 'auto';
    ambienceElRef.current = el;
  }, [AMBIENCE_URL]);

  const fadeAmbience = useCallback((toRatio) => {
    // toRatio is 0..1 meaning "fraction of the user-chosen ambience volume".
    // Actual target = toRatio * ambienceVolRef.current, so the bowls fade in/out
    // to a subtle bed under speech — never a full-blast overlap.
    const el = ambienceElRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(1, toRatio)) * ambienceVolRef.current;
    if (target > 0 && el.paused) { el.play().catch(() => {}); }
    const start = el.volume;
    const t0 = performance.now();
    const dur = 2500;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      el.volume = start + (target - start) * p;
      if (p < 1) requestAnimationFrame(step);
      else if (target === 0) { try { el.pause(); } catch (e) { /* noop */ } }
    };
    requestAnimationFrame(step);
  }, []);

  // Live slider handler — updates the ambience volume WITHOUT touching voice.
  const changeAmbienceVol = useCallback((v) => {
    ambienceVolRef.current = v;
    setAmbienceVol(v);
    localStorage.setItem(AMBIENCE_VOL_KEY, String(v));
    const el = ambienceElRef.current;
    if (el && ambienceOnRef.current) el.volume = v;
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => {
        setPlaying(true);
        if (ambienceOnRef.current) { ensureAmbience(); fadeAmbience(1); }
      }).catch(() => {});
    } else {
      a.pause(); setPlaying(false); fadeAmbience(0);
    }
  }, [ensureAmbience, fadeAmbience]);

  const toggleAmbience = useCallback(() => {
    setAmbienceOn((prev) => {
      const next = !prev;
      ambienceOnRef.current = next;
      if (next) {
        ensureAmbience();
        if (audioRef.current && !audioRef.current.paused) fadeAmbience(1);
      } else {
        fadeAmbience(0);
      }
      return next;
    });
  }, [ensureAmbience, fadeAmbience]);

  // Tear down the audio context when the player closes.
  useEffect(() => () => {
    const ctx = audioCtxRef.current;
    if (ctx) { try { ctx.close(); } catch (e) { /* noop */ } }
  }, []);

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a) return;
    const t = Number(e.target.value);
    a.currentTime = t;
    setCurrent(t);
  };

  // Log the session (dedups onEnded + the manual Complete button). `auto` = it
  // played all the way through; otherwise it's the user tapping "Complete".
  const completedRef = useRef(false);
  const markComplete = useCallback((auto = false) => {
    if (completedRef.current) { if (!auto) onClose(); return; }
    completedRef.current = true;
    const a = audioRef.current;
    const dur = Math.round((a && (auto ? a.duration : a.currentTime)) || current || 0);
    api.completeMeditation({ meditation_id: meditationId, title: meta.title, duration_seconds: dur }).catch(() => {});
    window.dispatchEvent(new Event('wellness:refresh'));
    if (!auto) {
      toast.success('Session logged — nice work. Nikki has it.');
      onClose();
    }
  }, [meditationId, meta.title, current, onClose]);

  const pct = duration ? (current / duration) * 100 : 0;
  const scale = playing ? 1.18 : 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center safe-top safe-bottom"
      style={{ background: 'radial-gradient(circle at 50% 35%, #15233b 0%, #0a0f1a 55%, #05080f 100%)' }}
      data-testid="meditation-player"
    >
      <style>{`@keyframes med-breathe{0%,100%{transform:scale(1);opacity:.55}50%{transform:scale(1.12);opacity:.9}}`}</style>

      <button
        onClick={onClose}
        className="absolute z-20 p-3 -m-1 text-zinc-300 hover:text-white transition-colors"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
          right: 'calc(env(safe-area-inset-right, 0px) + 8px)',
        }}
        data-testid="meditation-close"
        aria-label="Close meditation"
      >
        <X className="w-6 h-6" />
      </button>

      <div className="text-center px-6 mb-2">
        <p className="text-[11px] text-cyan-300/70 uppercase tracking-[0.2em]">{meta.subtitle}</p>
        <h2 className="font-secondary font-bold text-2xl sm:text-3xl text-white mt-2" data-testid="meditation-title">
          {meta.title}
        </h2>
      </div>

      {/* Breathing orb */}
      <div className="relative my-10 flex items-center justify-center" style={{ width: 260, height: 260 }}>
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(34,211,238,0.35) 0%, rgba(34,211,238,0) 70%)',
            animation: playing ? 'med-breathe 7s ease-in-out infinite' : 'none',
            transform: `scale(${scale})`,
            transition: 'transform 1.2s ease-in-out',
          }}
        />
        <div
          className="absolute inset-10 rounded-full border flex items-center justify-center"
          style={{
            borderColor: 'rgba(34,211,238,0.5)',
            background: 'rgba(34,211,238,0.06)',
            transform: `scale(${scale})`,
            transition: 'transform 1.2s ease-in-out',
          }}
        >
          {status === 'loading' && (
            <div className="text-center" data-testid="meditation-loading">
              <Loader2 className="w-7 h-7 text-cyan-300 animate-spin mx-auto" />
              <p className="text-xs text-zinc-400 mt-3">Preparing your<br />meditation…</p>
              <p className="text-[10px] text-zinc-600 mt-2 px-4">First time can take<br />up to a minute</p>
            </div>
          )}
          {status === 'error' && (
            <div className="text-center px-6" data-testid="meditation-error">
              <p className="text-xs text-red-300 mb-3">
                Couldn&apos;t load the audio.
              </p>
              <button
                onClick={prepare}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 text-black text-xs font-bold hover:bg-cyan-300 transition-colors"
                data-testid="meditation-retry"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Try again
              </button>
            </div>
          )}
          {status === 'ready' && (
            <button
              onClick={toggle}
              className="w-20 h-20 rounded-full bg-cyan-400 text-black flex items-center justify-center shadow-lg hover:bg-cyan-300 transition-colors"
              data-testid="meditation-play-toggle"
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
            </button>
          )}
        </div>
      </div>

      {/* Scrubber + time */}
      <div className="w-full max-w-sm px-8">
        <input
          type="range"
          min={0}
          max={duration || 0}
          value={current}
          onChange={onSeek}
          disabled={status !== 'ready'}
          className="w-full h-1 appearance-none rounded-full cursor-pointer disabled:opacity-40"
          style={{ background: `linear-gradient(to right, #22d3ee ${pct}%, #334155 ${pct}%)` }}
          data-testid="meditation-scrubber"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="font-mono text-xs text-zinc-400">{fmt(current)}</span>
          <span className="font-mono text-xs text-zinc-400">{fmt(duration) === '0:00' && estMin ? `${estMin}:00` : fmt(duration)}</span>
        </div>
      </div>

      {/* Ambience toggle + Complete */}
      <div className="mt-7 flex items-center gap-3 flex-wrap">
        <button
          onClick={toggleAmbience}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs transition-colors ${ambienceOn ? 'border-cyan-400/50 text-cyan-200 bg-cyan-400/10' : 'border-zinc-700 text-zinc-500 hover:text-zinc-300'}`}
          data-testid="meditation-ambience-toggle"
        >
          <Bell className="w-4 h-4" />
          {ambienceOn ? 'Tibetan bowls · on' : 'Tibetan bowls · off'}
        </button>
        {ambienceOn && (
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-black/40"
            data-testid="meditation-ambience-volume"
          >
            <Bell className="w-3 h-3 text-cyan-200/70" />
            <input
              type="range" min="0" max="0.5" step="0.01"
              value={ambienceVol}
              onChange={(e) => changeAmbienceVol(parseFloat(e.target.value))}
              className="w-24 accent-cyan-400"
              aria-label="Ambience volume"
              data-testid="meditation-ambience-volume-slider"
            />
            <span className="text-[10px] font-mono text-cyan-200/80 tabular-nums w-8">
              {Math.round((ambienceVol / 0.5) * 100)}%
            </span>
          </div>
        )}
        <button
          onClick={() => markComplete(false)}
          disabled={status !== 'ready'}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-400 text-black text-xs font-bold hover:bg-cyan-300 transition-colors disabled:opacity-40"
          data-testid="meditation-complete-btn"
        >
          <Check className="w-4 h-4" /> Complete
        </button>
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          preload="auto"
          onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
          onTimeUpdate={(e) => setCurrent(e.target.currentTime || 0)}
          onEnded={() => {
            setPlaying(false); setCurrent(0); fadeAmbience(0);
            markComplete(true);
          }}
          data-testid="meditation-audio"
        />
      )}
    </div>
  );
};

export default MeditationPlayer;
